import { map, reduceRight } from 'ramda';
import { AnyWorkflowNode } from './workflowTree';

type VisitorFn = <N extends AnyWorkflowNode = AnyWorkflowNode>(
  node: N
) => AnyWorkflowNode;

interface RuntimeWorkflowVisitorFn {
  enter: VisitorFn;
  exit: VisitorFn;
}

export type WorkflowVisitorFn = Partial<RuntimeWorkflowVisitorFn> | VisitorFn;

interface RuntimeWorkflowVisitor<Fn = RuntimeWorkflowVisitorFn> {
  [key: string]: Fn;
}

export interface WorkflowVisitor
  extends RuntimeWorkflowVisitor<WorkflowVisitorFn | undefined> {
  TransitStep?: WorkflowVisitorFn;
  ModuleStep?: WorkflowVisitorFn;
  JunctionStep?: WorkflowVisitorFn;
  GroupStep?: WorkflowVisitorFn;
  GroupExitStep?: WorkflowVisitorFn;
  ChildStep?: WorkflowVisitorFn;
}

const basicVisitorFn: VisitorFn = n => n;

const basicVisitor: RuntimeWorkflowVisitorFn = {
  enter: basicVisitorFn,
  exit: basicVisitorFn,
};

const composeVisitorFn = reduceRight(
  (
    elm: Required<RuntimeWorkflowVisitorFn>,
    acc: Required<RuntimeWorkflowVisitorFn>
  ): Required<RuntimeWorkflowVisitorFn> => ({
    enter: n => elm.enter(acc.enter(n)),
    exit: n => acc.exit(elm.exit(n)),
  }),
  {
    enter: basicVisitorFn,
    exit: basicVisitorFn,
  }
);

const cleanVisitorFn = (
  visitorFn?: WorkflowVisitorFn
): RuntimeWorkflowVisitorFn => {
  const enter =
    typeof visitorFn === 'object' && visitorFn.enter !== undefined
      ? visitorFn.enter
      : typeof visitorFn === 'function'
      ? visitorFn
      : basicVisitorFn;
  const exit =
    typeof visitorFn === 'object' && visitorFn.exit !== undefined
      ? visitorFn.exit
      : basicVisitorFn;
  return {
    enter,
    exit,
  };
};

const composeVisitors = reduceRight(
  (elm: WorkflowVisitor, acc: RuntimeWorkflowVisitor) =>
    Object.keys(elm).reduce(
      (obj, key) => ({
        ...obj,
        [key]: composeVisitorFn([
          cleanVisitorFn(acc[key]),
          cleanVisitorFn(elm[key]),
        ]),
      }),
      {} as RuntimeWorkflowVisitor
    ),
  {
    TransitStep: basicVisitor,
    ModuleStep: basicVisitor,
  }
);

const traverseDepthFirst = (...visitors: WorkflowVisitor[]) => {
  const visitor = composeVisitors(visitors);
  const visitedNodes: { [key: string]: AnyWorkflowNode } = {};

  const visitNode = (node: AnyWorkflowNode) => {
    if (visitedNodes[node.id]) {
      return visitedNodes[node.id];
    } else {
      visitedNodes[node.id] = node;
      const visitorEnterFn = visitor[node.type]
        ? visitor[node.type].enter
        : basicVisitorFn;
      const visitorExitFn = visitor[node.type]
        ? visitor[node.type].exit
        : basicVisitorFn;

      const nodeAfterEnter: AnyWorkflowNode = visitorEnterFn(node);
      const nodeAfterChildren: AnyWorkflowNode = {
        ...nodeAfterEnter,
        next: map(visitNode, nodeAfterEnter.next),
      };
      const nodeAfterExit: AnyWorkflowNode = visitorExitFn(nodeAfterChildren);

      return nodeAfterExit;
    }
  };
  return visitNode;
};

export const WorkflowFns = {
  traverseDepthFirst,
};
